import React, { useCallback, useState } from "react";
import useInventoryAlerts from "./useInventoryAlerts";
import SnapshotBar from "./parts/SnapshotBar";
import ActionCenter from "./parts/ActionCenter";
import InventoryHealth from "./parts/InventoryHealth";
import InboundBoard from "./parts/InboundBoard";
import MoneyRecovery from "./parts/MoneyRecovery";
import SystemHealth from "./parts/SystemHealth";
import AlertSettings from "./parts/AlertSettings";
import SkuDrawer from "./parts/SkuDrawer";

const TABS = [
  { key: "actions", label: "Action center" },
  { key: "inventory", label: "Inventory health" },
  { key: "inbound", label: "Inbound" },
  { key: "recovery", label: "Money recovery" },
  { key: "settings", label: "Alert settings" },
  { key: "health", label: "System health" },
];

/**
 * Inventory alerts for one store.
 *   <InventoryAlertsPage profileId={3222612353096708} />
 * Place it as a tab on Store Overview, or as its own route.
 */
export default function InventoryAlertsPage({ profileId }) {
  const ia = useInventoryAlerts(profileId);
  const [tab, setTab] = useState("actions");
  const [sku, setSku] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const [showNotes, setShowNotes] = useState(false);
  const d = ia.data;
  const currency = (d && d.inventoryHealth && d.inventoryHealth.rows[0] && d.inventoryHealth.rows[0].currency) || "USD";

  const openTarget = useCallback((target) => {
    if (!target) return;
    if (target.view === "inventory") {
      setTab("inventory");
      setSku(target.sku);
    } else if (target.view === "inbound") {
      setTab("inbound");
      setHighlight(target.shipmentId || target.planId);
    } else if (target.view === "recovery") {
      setTab("recovery");
      setHighlight(target.claimId);
    } else if (target.view === "health") {
      setTab("health");
    }
  }, []);

  const counts = d
    ? {
        actions: d.actionCenter && d.actionCenter.kpis.actToday,
        inventory: d.inventoryHealth && d.inventoryHealth.kpis.stockoutBeforeRestock,
        inbound: d.inbound && d.inbound.kpis.actToday,
        recovery: d.moneyRecovery && d.moneyRecovery.kpis.windowClosingSoon.claims,
        health: d.systemHealth && d.systemHealth.row.level === "u" ? 1 : 0,
      }
    : {};

  return (
    <div className="ia-root">
      <header className="ia-head">
        <div>
          <h1 className="ia-title">Inventory alerts</h1>
          <div className="ia-storeline">{d ? d.store.name : ""}</div>
        </div>
        <SnapshotBar
          snapshot={d && d.snapshot}
          asOf={d && d.asOf}
          loading={ia.loading}
          onReload={() => ia.reload()}
          job={ia.job}
          jobRunning={ia.jobRunning}
          jobError={ia.jobError}
          onFetch={ia.startFetchJob}
          data={d}
          currentTab={tab}
          currency={currency}
        />
      </header>

      <nav className="ia-tabs" aria-label="Inventory alerts sections">
        {TABS.map((t) => (
          <button
            type="button"
            key={t.key}
            className="ia-tab"
            aria-current={tab === t.key ? "page" : undefined}
            onClick={() => {
              setTab(t.key);
              setHighlight(null);
            }}
          >
            {t.label}
            {counts[t.key] ? <span className="ia-tab__count">{counts[t.key]}</span> : null}
          </button>
        ))}
      </nav>

      {ia.error ? (
        <div className="ia-alert">
          Could not load inventory alerts: {ia.error}{" "}
          <button type="button" className="ia-link" onClick={() => ia.reload()}>
            Try again
          </button>
        </div>
      ) : null}

      {d && d.warnings && d.warnings.length ? (
        <div className="ia-notes">
          <button type="button" className="ia-link" onClick={() => setShowNotes((v) => !v)} aria-expanded={showNotes}>
            {d.warnings.length} data note{d.warnings.length > 1 ? "s" : ""}
          </button>
          {showNotes ? (
            <ul>
              {d.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {!d && ia.loading ? <div className="ia-card ia-empty">Loading inventory alerts…</div> : null}

      {d ? (
        <main className={`ia-main${ia.loading ? " ia-main--busy" : ""}`}>
          {tab === "actions" && d.actionCenter ? <ActionCenter data={d.actionCenter} currency={currency} onOpen={openTarget} /> : null}
          {tab === "inventory" && d.inventoryHealth ? (
            <InventoryHealth data={d.inventoryHealth} currency={currency} onOpenSku={setSku} />
          ) : null}
          {tab === "inbound" && d.inbound ? <InboundBoard data={d.inbound} highlight={highlight} /> : null}
          {tab === "recovery" && d.moneyRecovery ? (
            <MoneyRecovery data={d.moneyRecovery} currency={currency} highlight={highlight} />
          ) : null}
          {tab === "settings" && d.settings ? (
            <AlertSettings settings={d.settings} onApply={ia.applySettings} busy={ia.loading} />
          ) : null}
          {tab === "health" && d.systemHealth ? <SystemHealth data={d.systemHealth} /> : null}
        </main>
      ) : null}

      {sku ? <SkuDrawer sku={sku} loadSku={ia.loadSku} onClose={() => setSku(null)} currency={currency} /> : null}
    </div>
  );
}
